import React from 'react';
import Accordion from 'react-bootstrap/Accordion';
import BigHeader from '../../components/BigHeader';
import "bootstrap/dist/css/bootstrap.min.css";
import { Container, Row, Col, ListGroup, ListGroupItem, Table, Image } from 'react-bootstrap';

const OneAcc = ({text, component}) => {
    return (
        <Accordion>

        <Accordion.Item eventKey="0">
        <Accordion.Header>{text}</Accordion.Header>
        <Accordion.Body>
        {component}
        </Accordion.Body>
        </Accordion.Item>
        </Accordion>
    )
}
function Electrocompetent() {
    return (
        <Container>
                    <Row>
            <Col>
            <h2 id="description">Description</h2>
            <p>Similar to the chemical competent cell transformation protocol, the aim of this method is to induce cells into becoming compatible with foreign DNA uptake. However the difference lies in the driver for transmembrane DNA transfer, which in this case is a short electric pulse rather than a sudden change in temperature like what is used to transform chemically competent cells with DNA. Unlike chemical competent cells, the membrane chemistry of electrocompetent cells is not altered but the ions present in their surroundings are removed by repetitive washing and they usually are more efficient in receiving DNA.</p>
            </Col>
          </Row>
          <Row>
            <Col>
              <h2 id="materials">Materials</h2>
              <ListGroup>
                <ListGroupItem>Electroporation cuvette (one per reaction)</ListGroupItem>
                <ListGroupItem><em>E. coli</em> cells</ListGroupItem>
                <ListGroupItem>
                  Sterile (filter-sterilized) miliQ water (ddH20) + 10% (v/v) glycerol. <em>E.g.</em> 100mL of pure glycerol in 900mL of water.
                </ListGroupItem>
                <ListGroupItem>LB (liquid)</ListGroupItem>
                <ListGroupItem>LB plate + appropriate antibiotic for selection</ListGroupItem>
              </ListGroup>
            </Col>
          </Row>
          <Row>
            <Col>
              <h2 id="methods">Methods</h2>
              <ol>
                <li>Grow an overnight culture of each strain in LB medium. (Inoculate a colony into 3-5mL)</li>
                <li>Prepare 100 ml of fresh LB medium in a 500 ml flask for each strain.</li>
                <li>Inoculate with overnight, stationary-phase culture to an OD of ~0.05. (100μl of overnight culture into 100mL fresh LB)</li>
                <li>Grow the cells for approximately 2-3 hours, until they reach an OD between 0.6 and 0.7
                  <ul>
                    <li><strong>~30 min before you plan to prepare your cells, set the centrifuge to 4C.</strong></li>
                  </ul>
                </li>
                <li>Once the cells have reached OD=0.6, place the flask in ice for 10min.
                  <ul>
                    <li><strong>From now on, cells must stay in ice. Every tube and solution must be chilled. Cells need to stay on ice at all times.</strong></li>
                  </ul>
                </li>
                <li>Transfer the cells to 2x 50 ml Falcon conical tubes.</li>
                <li>Pellet the cells by centrifugation for 5 minutes at max speed at 4C. Remove promptly, put back on ice, and pour off supernatant into a trash.</li>
                <li>Add 1mL ice-<strong>cold</strong> glycerol 10% with a 1000uL micropipette, pipette up and down to resuspend the pellet.</li>
                <li>Add ~40 ml of ice-<strong>cold</strong> 10% glycerol to each tube</li>
                <li>Repeat the wash cycles (Centrifuge, resuspension, dilution) in 10% glycerol two times more.</li>
                <li>Resuspend each pellet in approximately 500 μl of 10% glycerol to make a 100x concentration of the initial culture.</li>
                <li>Divide into 30µl aliquots in pre-chilled 1.5mL Eppendorf tubes.
                  <ul>
                    <li><strong>Cells to store</strong>: Flash-freeze in liquid nitrogen and store at -80C.</li>
                    <li><strong>Cells you want to use on the same day</strong>: skip to step 2 of the next part of the protocol. <strong>Keep on ice.</strong></li>
                  </ul>
                </li>
              </ol>
              <h3 id="transforming-e-coli-cells-by-electroporation">Transforming <em>E. coli</em> Cells by Electroporation</h3>
              <ol>
                <li>Thaw the electrocompetent cells <strong>on ice.</strong></li>
                <li>To the electrocompetent cells, add 1 μl of your DNA solution (~100 ng of DNA). Don’t forget to add a negative control (no DNA) and a positive control if it is the first time you’re using this batch of electrocompetent cells).</li>
                <li>Mix by gently flicking the tube containing the electrocompetent cell + DNA mixture. Let the mixture sit on ice for 1min.</li>
                <li>Pipette the mixture into a chilled cuvette (put it on ice for a few minutes), making sure that the mixture is at the bottom of the cuvette by placing the pipette tip toward the bottom (between the metallic slits).
                  <ul>
                    <li><strong>Be sure to wipe any condensation off the sides of the cuvette before electroporation with a dry paper towel.</strong></li>
                  </ul>
                </li>
                <li>Place the cuvette in the pulser. (in the right direction, there is a notch)
                  <ul>
                    <li>Use EC1 settings, and display the time constant in ms by pressing the “measurement” button twice.</li>
                    <li>Press the pulse button.
                      <ul>
                        <li>This will send a current in the cuvette, so don’t touch the cuvette. If you hear or see an electric arc, it means there is too much salt in your cells and the wash was not efficient.</li>
                      </ul>
                    </li>
                  </ul>
                </li>
                <li><strong>Quickly add</strong> 500μl of LB to the cuvette, pipette up and down to mix and recover the cells. If you add 500uL, you should recover 500uL.</li>
                <li>Transfer the mixture to standard culture tube with aeration which already contains 500uL LB.</li>
                <li>Write down the Time constant (ms) displayed on the screen of the micropulser in your notebook. A constant above 4.0ms is good. Below 3 it will usually not work well, or result in an electric arc.</li>
                <li>Incubate for 60 minutes at 37°C or other appropriate temperature in a shaking incubator.</li>
                <li>Plate the cells (~ 50-100 μl) on an LB plate containing the appropriate antibiotic.</li>
                <li>Incubate overnight at 37°C or other appropriate temperature.</li>
              </ol>
            </Col>
          </Row>
        </Container>
      );
}
function Chemicals()  {
    return (
        <Container>
          <Row>
            <Col>
            <h2 id="description">Description</h2>
            <p>The aim of this protocol is to transform wild type e coli cells which normally do not uptake DNA from their surroundings into competent cells by artificially altering cell membrane structures of these cells (induced by divalent and multivalent cations such as calcium, magnesium), making them porous and permeable to DNA plasmids. This particular protocol is adapted to <em>E. coli</em> strains.</p>

            </Col>
          </Row>
          <Row>
            <Col>
              <h2 id="materials">Materials</h2>
              <ListGroup>
                <ListGroupItem>LB</ListGroupItem>
                <ListGroupItem>Centrifuge</ListGroupItem>
                <ListGroupItem>Ice</ListGroupItem>
                <ListGroupItem>Selective media plates</ListGroupItem>
                <ListGroupItem>
                  TSS (100 mL):
                  <ul>
                    <li>10 g PEG 3350</li>
                    <li>5 mL DMSO</li>
                    <li>2 mL 1 M MgCL 2</li>
                    <li>LB to 100 mL</li>
                    <li>Filter sterilize and chill to 4 degrees C</li>
                  </ul>
                </ListGroupItem>
              </ListGroup>
            </Col>
          </Row>
          <Row>
            <Col>
              <h2 id="methods">Methods</h2>
              <ol>
                <li>Grow overnight culture from a single colony</li>
                <li>Inoculate 1:100 in 250 mL LB (or whatever volume you want, you'll get 1 aliquot per mL of LB)</li>
                <li>Grow to OD 600 ~0.35 <strong>(important that it be close to 0.35, 0.3-0.4 is good)</strong></li>
                <li>Chill on ice for 20 min (can chill for a few hours)</li>
                <li>Centrifuge at 8000g for 8 minutes, decant supernatant</li>
                <li>Resuspend by pipetting gently in 25 mL of ice-cold TSS. It can take a while to break up the pellet (I use a 25 mL pipet and scrape the pellet off the side of the tube before pipetting).</li>
                <li>Freeze 100 ul aliquots in liquid nitrogen &amp; store at -80 degrees C</li>
              </ol>
              <p>If you're making competent cells that do not yet have a plasmid (in other words, that lack an antibiotic-resistant cassette)</p>
              <h3 id="transforming-chemical-competent-cells">Transforming chemical competent cells</h3>
              <ol>
                <li>Thaw aliquots on ice</li>
                <li>
                  To a 100 ul aliquot of cells, add:
                  <ul>
                    <li>DNA (or 1-10 uL ligation mixture)</li>
                    <li>20 ul 5X KCM (0.5 M KCl, 0.15 M CaCl 2, 0.25 M MgCl2)</li>
                    <li>sterile water to 200 uL</li>
                  </ul>
                </li>
                <li>Incubate on ice for 5-30 minutes (longer incubation gives more colonies)</li>
                <li>Heat shock at 42C degrees for 90 seconds</li>
                <li>Return cells to ice for 1-2 minutes</li>
                <li>Add 400 uL LB to cells. Recover the tube at 37C degrees, with shaking, for 30-60 minutes (only 30 if your selection marker is ampicillin).</li>
                <li>Plate on selective medium and incubate at 37C degrees overnight</li>
              </ol>
            </Col>
          </Row>
        </Container>
      );
}

function Gibson() {
    return (
        <Container>
                              <Row>
            <Col>
            <h2 id="description">Description</h2>
            <p>Gibson assembly is a plasmid cloning method that is used to assemble two or more fragments of DNA without using restriction enzymes, making it a relatively rapid and cost effective cloning method. Instead, the ‘tail’ regions of these fragments are designed in a way that they are complimentary to each other. The complimentary regions overlap and ligate in the right conditions (mediated by temperature) and the master mix mediates terminal homologous overlap, annealing, followed by extension and ligation resulting in a complete plasmid. This particular protocol is shaped by NEBuilder® HiFi DNA Assembly protocol due to the particular master mix used in our experiments.</p>

            </Col>
          </Row>
          <Row>
            <Col>
              <h2 id="materials">Materials</h2>
              <ListGroup>
                <ListGroupItem>50~100 ng of amplified vector backbone</ListGroupItem>
                <ListGroupItem>Amplified insert DNA (2:1 molar ratio compared to backbone)</ListGroupItem>
                <ListGroupItem>10 µL NEBuilder® HiFi DNA Assembly Master Mix</ListGroupItem>
                <ListGroupItem>Nuclease-free water</ListGroupItem>
              </ListGroup>
            </Col>
          </Row>
          <Row>
            <Col>
              <h2 id="methods">Methods</h2>
              <ol>
                <li>Measure the DNA concentration of insert and vector backbone using spectrophotometric methods such as NanoDrop.</li>
                <li>Calculate a 2:1 ~ 3:1 molar ratio of insert:backbone.</li>
                <li>In PCR tubes, mix appropriate volumes of DNA with NEBuilder® HiFi DNA Assembly Master Mix and make up to a total volume of 20 µL with nuclease-free water.</li>
                <li>Incubate tubes at 50°C for 45 min (ideally in a thermocycler for efficient incubation of small tubes).</li>
              </ol>
            </Col>
          </Row>
        </Container>
      );
    }


function PCA() {
  return (
        <Container>
         <Row>
            <Col>
              <h2 id="description">Description</h2>
              <p>PCA is a gene synthesis method for DNA lengths ranging from a hundreds to many thousands of base pairs. In this method, equimolar concentrations of single stranded DNA oligonucleotides undergo hybridization and extension into double stranded DNA in a process similar to a PCR.</p>
            </Col>
          </Row>
          <Row>
            <Col>
              <h2 id="materials">Materials</h2>
              <ul>
                <li>2 µL terminal primers (100 μM concentration)</li>
                <li>2 µL internal primers (1 μM concentration)</li>
                <li>25 µL Q5 polymerase 2x master mix</li>
                <li>Measure up to 50 µL total volume with nuclease-free water</li>
              </ul>
            </Col>
          </Row>
          <Row>
            <Col>
              <h2 id="pcr-conditions">PCR conditions</h2>
              <Table striped bordered>
                <thead>
                  <tr>
                    <th>Steps</th>
                    <th>Temperature</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Initial denaturation</td>
                    <td>98 °C</td>
                    <td>30 s</td>
                  </tr>
                  <tr>
                    <td>Denaturation</td>
                    <td>98 °C</td>
                    <td>10 s</td>
                  </tr>
                  <tr>
                    <td>Annealing</td>
                    <td>minimum Tm</td>
                    <td>30 s</td>
                  </tr>
                  <tr>
                    <td>Extension</td>
                    <td>72 °C</td>
                    <td>30 s</td>
                  </tr>
                  <tr>
                    <td>Repeat steps 2-4</td>
                    <td></td>
                    <td>34 cycles</td>
                  </tr>
                  <tr>
                    <td>Final extension</td>
                    <td>72 °C</td>
                    <td>10 min</td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>
        </Container>
      );
}

function ColonyPCA() {
    return (
        <Container>
                   <Row>
            <Col>
              <h2 id="description">Description</h2>
              <p>This method of PCR is used for rapid screening of bacterial colonies post-transformation to verify weather the intended genetic construct is successfully transformed into the colonies. It entails lysing the cells taken from a single colony to expose the plasmids inside to the primers present in the master mix. If the transformation is successful, the plasmid serves as the template for amplification as primers that are designed to anneal to flanking  regions of the insert bind and amplify the region of interest only if the insert is present in the correct orientation and sequence. The size of this region is then determined by gel electrophoresis. This particular protocol is adapted to the Thermo Scientific™ DreamTaq Green PCR Master Mix.</p>            </Col>
          </Row>
          <Row>
            <Col>
              <h2 id="materials">Materials</h2>
              <ListGroup>
                <ListGroupItem>2.5 µL terminal primers (10 μM concentration)</ListGroupItem>
                <ListGroupItem>2.5 µL internal primers (10 μM concentration)</ListGroupItem>
                <ListGroupItem>12.5 µL DreamTaq colony PCR master mix</ListGroupItem>
                <ListGroupItem>Measure up to 25 µL total volume with nuclease-free water</ListGroupItem>
                <ListGroupItem>Plated bacterial colonies</ListGroupItem>
              </ListGroup>
            </Col>
          </Row>
          <Row>
            <Col>
              <h2 id="methods">Methods</h2>
              <ol>
                <li>Decide on adequate primer pairs to perform the PCR reaction, and note down their ideal annealing temperature (the lowest Tm of the two).</li>
                <li>Label colonies to be assessed on the culture plate.</li>
                <li>Prepare and label PCR tubes with the adequate primer pairs.
                  <ol>
                    <li>One can prepare a large batch (enough for the number of samples +1 to account for some loss during aliquoting) of the PCR mix and aliquot, this will help with consistent polymerase / primer concentrations.</li>
                  </ol>
                </li>
                <li>For each sample, take a p10 micropipette and prick the colony with the pipette tip.
                  <ol>
                    <li>Just a few cells is enough for a successful PCR, so there is no need to scrape the colonies, a little touch will do.</li>
                  </ol>
                </li>
                <li>Place the pipette tip with the sample in the PCR tube with mix and stir/aspirate a few times to release the cells from the tip.</li>
                <li>Securely place tubes in a thermocycler and run the reaction as follows:</li>
              </ol>
            </Col>
          </Row>
          <Row>
            <Col>
              <h2 id="pcr-conditions">PCR conditions</h2>
              <Table striped bordered>
                <thead>
                  <tr>
                    <th>Steps</th>
                    <th>Temperature</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Initial denaturation</td>
                    <td>95 °C</td>
                    <td>5 min</td>
                  </tr>
                  <tr>
                    <td>Denaturation</td>
                    <td>95 °C</td>
                    <td>30 s</td>
                  </tr>
                  <tr>
                    <td>Annealing</td>
                    <td>minimum Tm</td>
                    <td>30 s</td>
                  </tr>
                  <tr>
                    <td>Extension</td>
                    <td>72 °C</td>
                    <td>1 min per Kb</td>
                  </tr>
                  <tr>
                    <td>Repeat steps 2-4</td>
                    <td></td>
                    <td>34 cycles</td>
                  </tr>
                  <tr>
                    <td>Final extension</td>
                    <td>72 °C</td>
                    <td>5 min</td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>
        </Container>
      );
}

function goldenGate() {
    return (
        <Container>
          <Row>
            <Col>
              <h2 id="description">Description</h2>
              <p>Goldengate cloning is another type of method used to assemble a plasmid. It differs from Gibson assembly as it makes use of restriction enzymes that bind and cleave restriction sites that need to be designed into the plasmid. The integration of this step makes cloning of DNA inserts derived from diverse samples more convenient as the only design requirement to the gene fragments is the addition of the restriction sites. The constraint to this method is the number of fragments that can be assembled at once as well as the absence of type IIS restriction sites within the fragment sequence itself.</p>
              </Col>
          </Row>
          <Row>
            <Col>
              <h2 id="materials">Materials</h2>
              <ListGroup>
                <ListGroupItem>DNA of the backbone</ListGroupItem>
                <ListGroupItem>DNA of the insert</ListGroupItem>
                <ListGroupItem>2 µl T4 buffer</ListGroupItem>
                <ListGroupItem>0.5 µl T4 ligase (Cons.)</ListGroupItem>
                <ListGroupItem>0.5-1 µl of DpnI</ListGroupItem>
                <ListGroupItem>1 µl BsaI HF v2</ListGroupItem>
              </ListGroup>
            </Col>
          </Row>
          <Row>
            <Col>
              <h2 id="methods">Methods</h2>
              <ol>
                <li>Prepare and label PCR tubes</li>
                <li>Use ~ 100 ng of the backbone and proportional to its size, add of the insert. For example, if the insert is half the size of the backbone, use half ng of it.</li>
                <li>Add 2ul of the T4 buffer + + 0.5 ul T4 ligase (Cons. ) +1 µl of DpnI + 1 µl BsaI HF v2</li>
                <li>Fill the PCR tube to a final volume of 20 ul</li>
                <li>Carefully pipette; avoid any vortexing</li>
              </ol>
            </Col>
          </Row>
          <Row>
            <Col>
              <h2 id="goldengate-conditions">Goldengate conditions</h2>
              <Table striped bordered>
                <thead>
                  <tr>
                    <th>Temperature</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>37 °C</td>
                    <td>60 min</td>
                  </tr>
                  <tr>
                    <td>16 °C</td>
                    <td>60 min</td>
                  </tr>
                  <tr>
                    <td>50 °C</td>
                    <td>20 min (Only use this in case of MoClo plasmids are used)</td>
                  </tr>
                  <tr>
                    <td>80 °C</td>
                    <td>20 min</td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>
        </Container>
      );
}


function Amplification() {
    return (
        <Container>
          <Row>
            <Col>
            <h2 id="description">Description</h2>
            <p>Phage amplification is a method to obtain larger quantities of a bacteriophage from an isolate or a stock. This method exploits an E. coli culture growing in its exponential phase to facilitate phage infection and amplification in  to increase the number of phages in the solution. This protocol can only be used to amplify lytic phages and the isolation host can vary depending upon the phage type. After phage amplification, the titer of the new solution can be used get a quantitative estimate of the number of phages per unit volume.</p>
            </Col>
          </Row>
          <Row>
            <Col>
              <h2 id="materials">Materials</h2>
              <ListGroup>
                <ListGroup.Item>500mL LB Lennox Broth medium (BD Difco – ref. 240230 - 500g)</ListGroup.Item>
                <ListGroup.Item>1 agar plate LB Lennox Agar (BD Difco – ref. 240110 - 500g)</ListGroup.Item>
                <ListGroup.Item>1 bottle 500mL</ListGroup.Item>
                <ListGroup.Item>2 x 50mL Falcon tube</ListGroup.Item>
                <ListGroup.Item>Spectrophotometer</ListGroup.Item>
                <ListGroup.Item>2 Single-use filtration units (0.45μM, PES membrane, capacity 500mL; ref. 514-0341 VWR)</ListGroup.Item>
                <ListGroup.Item>1 Single-use filtration unit (0.22μM, PES membrane, capacity 500mL ref. 514-0340 VWR)</ListGroup.Item>
                <ListGroup.Item>1 Pipette 10mL</ListGroup.Item>
                <ListGroup.Item>2 Bottle 100mL</ListGroup.Item>
                <ListGroup.Item>Vacuum pump</ListGroup.Item>
                <ListGroup.Item>Centrifuge</ListGroup.Item>
              </ListGroup>
            </Col>
          </Row>
          <Row>
            <Col>
              <h2 id="methods">Methods</h2>
              <ul>
                <li>The day before, prepare a culture <em>Overnight</em> of the host bacterium: 1 fresh colony in 5mL of LB; culture at 37°C stirring 200rpm <em>Overnight</em></li>
                <li>The next day;
                  <ul>
                    <li>Return to exponential culture (H0): dilute to 1/100e (500μL of the <em>overnight</em> culture in 50mL of LB - use a partially screwed cap bottle of 500mL); culture at 37°C 200rpm</li>
                    <li>Bacterial lysis:
                      <ul>
                        <li>Addition of the bacteriophage to be amplified when the DO600nm = 0.1-0.2 (≈H2) to MOI 10-²
                          <ul>
                            <li>Bacteria: 50mL x 5.107 CFU/mL = 2.5.10 9 CFU</li>
                            <li>Phage : 2,5.107 PFU</li>
                          </ul>
                        </li>
                        <li>Continuation of cultivation at 37°C stirring 200rpm until the culture is clear – DO&lt;0.05 (≈H6-8)</li>
                        <li>NB: for some bacteria-bacteriophage pairs, the MOI must be lower up to 10-4; sometimes the lysis is insufficient despite the amplification of the phage to lighten the solution.</li>
                      </ul>
                    </li>
                    <li>Centrifuge the lysate: 7500g for 20min at 4 ° C in a falcon tube of 50mL</li>
                    <li>Recover the supernatant in a sterile 500mL vial
                      <ul>
                        <li>Store at 4°C for filtration the next day</li>
                        <li>Titrate the lysate</li>
                      </ul>
                    </li>
                  </ul>
                </li>
              </ul>
            </Col>
          </Row>
          <Row>
            <Col>
              <p><strong>Filtration</strong></p>
              <ol>
                <li>Filter samples at 0.45μm</li>
              <p>The 0.45μm filter may become clogged from 200 to 300mL: plan to use a second filter for the remaining 200 to 300mL</p>
                <li>Filter samples at 0.22μm</li>
              </ol>
            </Col>
          </Row>
        </Container>
      );
}
function Plaque() {
    return (
        <Container>
                    <Row>
            <Col>
            <h2 id="description">Description</h2>
            <p>Plaque assays provide visual, quantitative data about the extent of phage infection. The basic principle behind it is introducing phages to a solid yet diffusible lawn of bacteria growing in agar, leading to infection and lysis of the cells. A serially diluted phage solution is used to resolve plaque formation (due to phage induced cell lysis) to an extent where distinct plaques, each caused by singular virions are visible. The dilution factor as well as the number of plaques enable a quantification of phage infection.</p>            </Col>
          </Row>
          <Row>
            <Col>
              <h2 id="materials">Materials</h2>
              <ListGroup>
                <ListGroup.Item>
                  <ul>
                  <li>MMBA medium: LB 0.5% agar (1:1 of LB + LBA)</li>

                    <li>0.1 mM MnCl2 (100 μL of 1M stock solution per 1L medium)</li>
                    <li>5 mM MgCl2 (5 mL of 1M stock solution per 1L medium)</li>
                    <li>5 mM CaCl2 (5 mL of 1M stock solution per 1L medium)</li>
                  </ul>
                </ListGroup.Item>
                <ListGroup.Item>Square plates</ListGroup.Item>
                <ListGroup.Item>Falcon tubes</ListGroup.Item>
                <ListGroup.Item>Round bottom 96 well plate</ListGroup.Item>
                <ListGroup.Item>Phage stocks</ListGroup.Item>
                <ListGroup.Item>Multichannel pipette</ListGroup.Item>
              </ListGroup>
            </Col>
          </Row>
          <Row>
            <Col>
              <h2 id="methods">Methods</h2>
              <ol>
                <li>Inoculate a colony of the bacterial strain of interest in ±10 mL of LB medium with selective antibiotics.</li>
                <li>Incubate overnight at 37C with shaking.</li>
                <li>
                  For biological replicates, start at least 2 cultures with separate colonies. Example: for biological duplicates, use 2 cultures from 2 different colonies of the same strain.
                </li>
                <li>
                  Prepare phage stock dilutions of each phage to test dilutions from 10-3 to 10-8. Use a 96-deep well plate. Dilute phages with LB medium. Store at 4C. Standard phages: T7, T6, T5, T4, P1, lambda.
                </li>
                <li>Pay close attention not to cross-contaminate phage stocks and dilutions. Same phage stock dilutions can be reused for several experiments during a period of 2-3 weeks, if the stocks are well preserved.</li>
                <li>Prepare and label one 50 mL Falcon tube per plate per condition.</li>
                <li>Add 300 μL culture per tube.</li>
                <li>Add inducer at desired concentration (optional)</li>
                <li>After addition of inducer, quickly pour 35 mL of melted MMBA medium to each tube or vice versa and immediately pour into squared Petri dish.</li>
              </ol>
              <p>
                <strong>Phage infection</strong>
              </p>
              <ol>
                <li>Use multichannel pipette to place 5μL droplets of each phage on each plate.</li>
                <li>Tips can be reused, as long as the phage dilutions concentrations are increasing.</li>
                <li>Let droplets dry in PSM with lid open.</li>
                <li>Close plates and incubate upside-down at 37C.</li>
              </ol>
              <p>
                <strong>Measurement</strong>
              </p>
              <p>
                Phage infection is measured in plaque forming units per milliliter (PFU/ml). This value is calculated by: # of plaques at d x (1/μL lysate droplet) x 1000 x 10^d ; where d is the dilution factor after which no plaque is observed.
              </p>
            </Col>
          </Row>
        </Container>
      );
}

function bilayerAssay() {
    return (
        <Container>
          <Image fluid src={"https://static.igem.wiki/teams/4602/wiki/images/12-well-plaque-assay.png"}></Image>
          <Row>
            <Col>
              <h2 id="description">Description</h2>
              <p>
              The goal of this assay is to establish phage infectivity in the presence of a diffusion barrier between the phage solution and a bacterial lawn. To do this, a mixture of soft agar (MMBA) and the protein of interest is placed on top of a solid layer of agar containing e.coli in their exponential growth phase. A droplet containing phages is then placed on top of this second layer, and the growth of plaques in the first layer is observed over time. High phage concentrations work well for T4.              </p>
            </Col>
          </Row>
          <Row>
            <Col>
              <h2 id="materials">Materials</h2>
              <ListGroup>
                <ListGroup.Item>12 well plates</ListGroup.Item>
                <ListGroup.Item>Overnight cultures of…</ListGroup.Item>
                <ListGroup.Item>
                  MMBA medium: LB 0.5% agar (1:1 of LB + LBA), 0.1 mM MnCl2 (100 μL of 1M stock solution Per 1L medium), 5 mM MgCl2 (5mL of 1M stock solution Per 1L medium), 5 mM CaCl2 (5mL μL of 1M stock solution Per 1L medium)
                </ListGroup.Item>
                <ListGroup.Item>Mucin/protein dilutions</ListGroup.Item>
                <ListGroup.Item>T4 phage dilutions</ListGroup.Item>
              </ListGroup>
            </Col>
          </Row>
          <Row>
            <Col>
              <h2 id="methods">Methods</h2>
              <ol>
                <li>Prepare a total volume of the first layer, 1ml(per well) x 12= 12ml per plate.</li>
                <li>Aliquot the appropriate volume of MMBA into a Falcon tube and add m x 10 x 2μl of the overnight cultures into this tube, where m is the volume of MMBA in ml.</li>
                <li>Add 1ml of mixed agar into each well while ensuring no bubbles form as they will solidify. Wait for around 2 minutes till every well has a solid layer.</li>
                <li>For the next layer, each column of the 12 well plate will have a different dilution of mucin and each row will have different thickness of the mucin layer (different volume of the MMBA-mucin mixture) corresponding to that dilution.</li>
                <li>Prepare a 1:1 solution of MMBA and mucin/protein dilutions. The total volume of the dilutions should be calculated before the experiment depending on the volume of the second layer used in each well. Dry for 2 minutes.</li>
                <li>Pipette this solution onto the first layer in the wells while ensuring no bubbles remain and the second layer coats the first layer completely.</li>
                <li>Let this dry and proceed to adding phages without delay.</li>
                <li>From a pre-prepared serial dilution of phages, gently, without touching the mucin layer, add 10μl of phage solution onto the center of each of the wells containing the.</li>
                <li>Dry the phage droplets under the hood for an hour.</li>
                <li>Incubate at 37 degrees. Plaques usually take 72 hours to develop well.</li>
              </ol>
            </Col>
          </Row>
        </Container>
      );
}

function BuffersPurification() {
  return (
      <Container>
        <p>
          Our buffer preparation and protein purification is done according to{' '}
          <strong>OnePot PURE proteins purification (
            <a href="https://www.jove.com/fr/t/62625/onepot-pure-cell-free-system**).**">
              https://www.jove.com/fr/t/62625/onepot-pure-cell-free-system**).
            </a>
          </strong>
        </p>
  
        <Table id="27be4ed1-e039-463c-9ad5-eae22d15935e" responsive className="simple-table" bordered>
							<tbody>
								<tr id="e01d2463-2761-45f5-aa9d-66727457f785">
									<td id="Ld_[" className=""><strong>Protein solution purification components</strong>
									</td>
									<td id="o}b@" className=""><strong>Stock solution</strong></td>
									<td id="WH&gt;E" className=""></td>
									<td id="h|}X" className="" style={{width:"131px"}}><strong>Buffer A</strong></td>
									<td id="|iPt" className=""></td>
									<td id="}`=g" className=""></td>
									<td id="Kwno" className=""></td>
									<td id="}MMX" className=""><strong>Buffer B</strong></td>
									<td id="yLD[" className=""></td>
									<td id="J?v]" className=""></td>
									<td id="DPCh" className=""></td>
									<td id="OeUV" className=""><strong>Buffer HT</strong></td>
									<td id="~m~u" className=""></td>
									<td id=";Upf" className=""></td>
									<td id="vp?Z" className=""></td>
									<td id="MNV_" className=""><strong>Stock buffer A</strong></td>
									<td id="{wvY" className=""></td>
									<td id="?ynn" className=""></td>
									<td id="?AF[" className=""></td>
									<td id="Efiu" className=""><strong>Stock buffer B</strong></td>
									<td id="WQVi" className=""></td>
									<td id="ZSPG" className=""></td>
									<td id="xhl[" className=""></td>
								</tr>
								<tr id="f774c657-f32e-4b06-be44-a07474cd3978">
									<td id="Ld_[" className=""></td>
									<td id="o}b@" className="">MW [g/mol]</td>
									<td id="WH&gt;E" className="">Concentration [M]</td>
									<td id="h|}X" className="" style={{width:"131px"}}>concentration</td>
									<td id="|iPt" className=""></td>
									<td id="}`=g" className="">volume</td>
									<td id="Kwno" className=""></td>
									<td id="}MMX" className="">concentration</td>
									<td id="yLD[" className=""></td>
									<td id="J?v]" className="">volume</td>
									<td id="DPCh" className=""></td>
									<td id="OeUV" className="">concentration</td>
									<td id="~m~u" className=""></td>
									<td id=";Upf" className="">volume</td>
									<td id="vp?Z" className=""></td>
									<td id="MNV_" className="">concentration</td>
									<td id="{wvY" className=""></td>
									<td id="?ynn" className="">volume</td>
									<td id="?AF[" className=""></td>
									<td id="Efiu" className="">concentration</td>
									<td id="WQVi" className=""></td>
									<td id="ZSPG" className="">volume</td>
									<td id="xhl[" className=""></td>
								</tr>
								<tr id="35213190-b4f8-4296-a197-639bf03a3ced">
									<td id="Ld_[" className="">HEPES**</td>
									<td id="o}b@" className=""></td>
									<td id="WH&gt;E" className="">1</td>
									<td id="h|}X" className="" style={{width:"131px"}}>50</td>
									<td id="|iPt" className="">mM</td>
									<td id="}`=g" className="">25</td>
									<td id="Kwno" className="">mL</td>
									<td id="}MMX" className="">50</td>
									<td id="yLD[" className="">mM</td>
									<td id="J?v]" className="">25</td>
									<td id="DPCh" className="">mL</td>
									<td id="OeUV" className="">50</td>
									<td id="~m~u" className="">mM</td>
									<td id=";Upf" className="">10</td>
									<td id="vp?Z" className="">mL</td>
									<td id="MNV_" className="">50</td>
									<td id="{wvY" className="">mM</td>
									<td id="?ynn" className="">5</td>
									<td id="?AF[" className="">mL</td>
									<td id="Efiu" className="">50</td>
									<td id="WQVi" className="">mM</td>
									<td id="ZSPG" className="">5</td>
									<td id="xhl[" className="">mL</td>
								</tr>
								<tr id="8404b246-31a3-444a-b7d2-1337957a32f4">
									<td id="Ld_[" className="">Ammonium chloride*</td>
									<td id="o}b@" className="">53.49</td>
									<td id="WH&gt;E" className="">1</td>
									<td id="h|}X" className="" style={{width:"131px"}}>1000</td>
									<td id="|iPt" className="">mM</td>
									<td id="}`=g" className="">26.7</td>
									<td id="Kwno" className="">g</td>
									<td id="}MMX" className=""></td>
									<td id="yLD[" className=""></td>
									<td id="J?v]" className=""></td>
									<td id="DPCh" className=""></td>
									<td id="OeUV" className=""></td>
									<td id="~m~u" className=""></td>
									<td id=";Upf" className=""></td>
									<td id="vp?Z" className=""></td>
									<td id="MNV_" className=""></td>
									<td id="{wvY" className=""></td>
									<td id="?ynn" className=""></td>
									<td id="?AF[" className=""></td>
									<td id="Efiu" className=""></td>
									<td id="WQVi" className=""></td>
									<td id="ZSPG" className=""></td>
									<td id="xhl[" className=""></td>
								</tr>
								<tr id="0a4a4ffc-f195-40d9-9a11-f652727f454c">
									<td id="Ld_[" className="">Magnesium chloride</td>
									<td id="o}b@" className=""></td>
									<td id="WH&gt;E" className="">1</td>
									<td id="h|}X" className="" style={{width:"131px"}}>10</td>
									<td id="|iPt" className="">mM</td>
									<td id="}`=g" className="">5</td>
									<td id="Kwno" className="">mL</td>
									<td id="}MMX" className="">10</td>
									<td id="yLD[" className="">mM</td>
									<td id="J?v]" className="">5</td>
									<td id="DPCh" className="">mL</td>
									<td id="OeUV" className="">10</td>
									<td id="~m~u" className="">mM</td>
									<td id=";Upf" className="">2</td>
									<td id="vp?Z" className="">mL</td>
									<td id="MNV_" className="">10</td>
									<td id="{wvY" className="">mM</td>
									<td id="?ynn" className="">1</td>
									<td id="?AF[" className="">mL</td>
									<td id="Efiu" className="">10</td>
									<td id="WQVi" className="">mM</td>
									<td id="ZSPG" className="">1</td>
									<td id="xhl[" className="">mL</td>
								</tr>
								<tr id="3b3505c6-c30f-46b2-a767-11f2774d9a96">
									<td id="Ld_[" className="">Potassium chloride</td>
									<td id="o}b@" className="">74.55</td>
									<td id="WH&gt;E" className="">2</td>
									<td id="h|}X" className="" style={{width:"131px"}}></td>
									<td id="|iPt" className=""></td>
									<td id="}`=g" className=""></td>
									<td id="Kwno" className=""></td>
									<td id="}MMX" className="">100</td>
									<td id="yLD[" className="">mM</td>
									<td id="J?v]" className="">25</td>
									<td id="DPCh" className="">mL</td>
									<td id="OeUV" className="">100</td>
									<td id="~m~u" className="">mM</td>
									<td id=";Upf" className="">10</td>
									<td id="vp?Z" className="">mL</td>
									<td id="MNV_" className="">100</td>
									<td id="{wvY" className="">mM</td>
									<td id="?ynn" className="">5</td>
									<td id="?AF[" className="">mL</td>
									<td id="Efiu" className="">100</td>
									<td id="WQVi" className="">mM</td>
									<td id="ZSPG" className="">5</td>
									<td id="xhl[" className="">mL</td>
								</tr>
								<tr id="731a1a3c-d1ec-432b-a679-a86bb36295a8">
									<td id="Ld_[" className="">Imidazole (pH=7)</td>
									<td id="o}b@" className="">68.08</td>
									<td id="WH&gt;E" className="">1</td>
									<td id="h|}X" className="" style={{width:"131px"}}></td>
									<td id="|iPt" className=""></td>
									<td id="}`=g" className=""></td>
									<td id="Kwno" className=""></td>
									<td id="}MMX" className="">500</td>
									<td id="yLD[" className="">mM</td>
									<td id="J?v]" className="">250</td>
									<td id="DPCh" className="">mL</td>
									<td id="OeUV" className=""></td>
									<td id="~m~u" className=""></td>
									<td id=";Upf" className=""></td>
									<td id="vp?Z" className=""></td>
									<td id="MNV_" className=""></td>
									<td id="{wvY" className=""></td>
									<td id="?ynn" className=""></td>
									<td id="?AF[" className=""></td>
									<td id="Efiu" className=""></td>
									<td id="WQVi" className=""></td>
									<td id="ZSPG" className=""></td>
									<td id="xhl[" className=""></td>
								</tr>
								<tr id="b51ef43a-793f-4287-b3c7-73b12b7eb10d">
									<td id="Ld_[" className="">Glycerol</td>
									<td id="o}b@" className=""></td>
									<td id="WH&gt;E" className=""></td>
									<td id="h|}X" className="" style={{width:"131px"}}></td>
									<td id="|iPt" className=""></td>
									<td id="}`=g" className=""></td>
									<td id="Kwno" className=""></td>
									<td id="}MMX" className=""></td>
									<td id="yLD[" className=""></td>
									<td id="J?v]" className=""></td>
									<td id="DPCh" className=""></td>
									<td id="OeUV" className=""></td>
									<td id="~m~u" className=""></td>
									<td id=";Upf" className=""></td>
									<td id="vp?Z" className=""></td>
									<td id="MNV_" className="">30%</td>
									<td id="{wvY" className="">(v/v)</td>
									<td id="?ynn" className="">30</td>
									<td id="?AF[" className="">mL</td>
									<td id="Efiu" className="">60%</td>
									<td id="WQVi" className="">(v/v)</td>
									<td id="ZSPG" className="">60</td>
									<td id="xhl[" className="">mL</td>
								</tr>
								<tr id="7dd6ff12-864e-43f8-a765-11fc75bc9e85">
									<td id="Ld_[" className="">TCEP**</td>
									<td id="o}b@" className=""></td>
									<td id="WH&gt;E" className="">0.5</td>
									<td id="h|}X" className="" style={{width:"131px"}}>1</td>
									<td id="|iPt" className="">mM</td>
									<td id="}`=g" className="">1</td>
									<td id="Kwno" className="">mL</td>
									<td id="}MMX" className="">1</td>
									<td id="yLD[" className="">mM</td>
									<td id="J?v]" className="">1</td>
									<td id="DPCh" className="">mL</td>
									<td id="OeUV" className="">1</td>
									<td id="~m~u" className="">mM</td>
									<td id=";Upf" className="">0.4</td>
									<td id="vp?Z" className="">mL</td>
									<td id="MNV_" className="">1</td>
									<td id="{wvY" className="">mM</td>
									<td id="?ynn" className="">0.20</td>
									<td id="?AF[" className="">mL</td>
									<td id="Efiu" className="">1</td>
									<td id="WQVi" className="">mM</td>
									<td id="ZSPG" className="">0.20</td>
									<td id="xhl[" className="">mL</td>
								</tr>
								<tr id="19442330-2339-4f86-9d71-eb797a3775b3">
									<td id="Ld_[" className="">total buffer volume</td>
									<td id="o}b@" className=""></td>
									<td id="WH&gt;E" className=""></td>
									<td id="h|}X" className="" style={{width:"131px"}}></td>
									<td id="|iPt" className=""></td>
									<td id="}`=g" className="">500</td>
									<td id="Kwno" className="">mL</td>
									<td id="}MMX" className=""></td>
									<td id="yLD[" className=""></td>
									<td id="J?v]" className="">500</td>
									<td id="DPCh" className="">mL</td>
									<td id="OeUV" className=""></td>
									<td id="~m~u" className=""></td>
									<td id=";Upf" className="">200</td>
									<td id="vp?Z" className="">mL</td>
									<td id="MNV_" className=""></td>
									<td id="{wvY" className=""></td>
									<td id="?ynn" className="">100</td>
									<td id="?AF[" className="">mL</td>
									<td id="Efiu" className=""></td>
									<td id="WQVi" className=""></td>
									<td id="ZSPG" className="">100</td>
									<td id="xhl[" className="">mL</td>
								</tr>
							</tbody>
						
      </Table>
  
        <Table bordered>
        <tbody>
								<tr id="5659531a-f179-40dd-81b6-b5450c509f55">
									<td id="{H~j" className=""><strong>Chemical components</strong></td>
									<td id="N\v~" className=""><strong>Chemical lysis buffer</strong></td>
								</tr>
								<tr id="d9dda8c5-6b8c-4ebd-be0f-6df199452aeb">
									<td id="{H~j" className=""></td>
									<td id="N\v~" className="">Concentration</td>
								</tr>
								<tr id="5a16ddba-5ddd-40bd-843d-58c756b1d376">
									<td id="{H~j" className="">Urea</td>
									<td id="N\v~" className="">2 M</td>
								</tr>
								<tr id="e081f814-0503-4fc7-bc04-863322cb63a5">
									<td id="{H~j" className="">Titron X</td>
									<td id="N\v~" className="">1% </td>
								</tr>
								<tr id="3b0d1c96-5865-46e4-8a13-32a1112c4921">
									<td id="{H~j" className="">trisHCL</td>
									<td id="N\v~" className="">0.5 M pH = 8</td>
								</tr>
							</tbody>
        </Table>
  
        <Table bordered>
        <tbody>
								<tr id="21191b16-0053-4b51-a2a7-a89bf14abec6">
									<td id="Kdr&lt;" className=""><strong>Stock solutions</strong></td>
									<td id="wFbj" className=""></td>
									<td id="~qnM" className=""></td>
									<td id="&gt;HP\" className=""></td>
									<td id="f`sC" className=""></td>
									<td id="V:~C" className=""></td>
								</tr>
								<tr id="be0914ad-4d80-4b55-95ba-44d1a79744bf">
									<td id="Kdr&lt;" className=""></td>
									<td id="wFbj" className="">Mw [g/mol]</td>
									<td id="~qnM" className="">Concentration [M]</td>
									<td id="&gt;HP\" className="">Volume [L]</td>
									<td id="f`sC" className="">Mass [g]</td>
									<td id="V:~C" className="">Notes</td>
								</tr>
								<tr id="a9c5ec16-f60b-449e-9460-8081ad70344b">
									<td id="Kdr&lt;" className="">Magnesium acetate</td>
									<td id="wFbj" className="">214.45</td>
									<td id="~qnM" className="">1</td>
									<td id="&gt;HP\" className="">0.25</td>
									<td id="f`sC" className="">53.6</td>
									<td id="V:~C" className="">Add the required mass, fill with water to the required
										volume. Filter sterile and store at 4ºC</td>
								</tr>
								<tr id="80949703-a87a-4249-8cf0-ed62e50a65d8">
									<td id="Kdr&lt;" className="">Potassium chloride</td>
									<td id="wFbj" className="">74.55</td>
									<td id="~qnM" className="">2</td>
									<td id="&gt;HP\" className="">1</td>
									<td id="f`sC" className="">149.1</td>
									<td id="V:~C" className="">Add the required mass, fill with water to the required
										volume.</td>
								</tr>
								<tr id="5817b482-0589-463a-bb04-b76739071c12">
									<td id="Kdr&lt;" className="">Ammonium chloride</td>
									<td id="wFbj" className="">53.49</td>
									<td id="~qnM" className="">1</td>
									<td id="&gt;HP\" className="">1</td>
									<td id="f`sC" className="">53.5</td>
									<td id="V:~C" className="">Add the required mass, fill with water to the required
										volume.</td>
								</tr>
								<tr id="22b820d1-9a4e-4e9e-af3a-2580e45863ab">
									<td id="Kdr&lt;" className="">Imidazole (pH=7)</td>
									<td id="wFbj" className="">68.08</td>
									<td id="~qnM" className="">1</td>
									<td id="&gt;HP\" className="">1</td>
									<td id="f`sC" className="">68.1</td>
									<td id="V:~C" className="">Add the required mass, fill with water to 80%, adjust the pH
										to 7 with HCl, and fill with water to the required volume.</td>
								</tr>
								<tr id="162880b5-f8a7-4353-a60a-79b654f5fe8a">
									<td id="Kdr&lt;" className="">TrisHCl (pH=7.6) (TrisBase, HCl)</td>
									<td id="wFbj" className="">121.136</td>
									<td id="~qnM" className="">1</td>
									<td id="&gt;HP\" className="">0.5</td>
									<td id="f`sC" className="">60.6</td>
									<td id="V:~C" className="">Add the required mass of TrisBase, fill with water to 80%,
										adjust the pH to 7.6 with HCl, and fill with water to the required volume.</td>
								</tr>
							</tbody>
        </Table>
      </Container>
    );  
}

function PhageRetention() {
  return (
    <div>
    <h2 id="0742413d-08b6-4b5a-97ff-3e93bcecfbc6" class="">Description</h2>
    <p id="ec8530fb-80da-48eb-b02e-cea4509f7350" class="">This is a method of quantifying the
      binding of proteins to phage particles, devised through our engineering cycle. Essentially a
      pull-down interaction assay followed by titering of the phage. The binding of protein is
      measured as the amount of phage particles that are passed through.</p>
    <h2 id="15f725df-fd22-4586-a3d4-a459c7c42805" class="">Materials</h2>
    <ul id="5e218cae-b142-49c7-b0f1-c86117742672" class="bulleted-list">
      <li style={{listStyleType:"disc"}}>Sample phage stock solution</li>
    </ul>
    <ul id="45949ef6-9670-4441-975f-6570f263af40" class="bulleted-list">
      <li style={{listStyleType:"disc"}}>Purified “bait” protein in neutral buffer free of imidazole
      </li>
    </ul>
    <ul id="0d13522d-b97a-4734-a5b8-1994fa9b7154" class="bulleted-list">
      <li style={{listStyleType:"disc"}}>* Materials for pull-down protein interaction assay</li>
    </ul>
    <ul id="682864de-52d5-485a-87d4-1d4aba245d70" class="bulleted-list">
      <li style={{listStyleType:"disc"}}>* Materials for phage plaque assay</li>
    </ul>
    <h2 id="c255a080-ecbd-4313-9e3b-a783d0b6af9a" class="">Methods</h2>
    <ol type="1" id="d61ce9df-1cee-475a-ad69-82c23dd432be" class="numbered-list" start="1">
      <li>Prepare dilutions of purified bait protein and phage stock solution, in neutral buffer
        free of imidazole. As a ballmark, the concentration ratio of protein to phage should be
        high enough to retain a significant number of the comparitively heavy phage particles.
        We started with a molecular ratio of 10,000 : 1 of bait to phage respectively, but we
        recommend a higher ratio for uncharacterised or weak binding proteins.</li>
    </ol>
    <ol type="1" id="63ede7f7-e407-414c-ba53-992e0d0b789b" class="numbered-list" start="2">
      <li>Incubate 200 µL bait protein with 200 µL phage solution for 1 hr at 30 °C with shaking.
        <ol type="a" id="25e396c0-c299-493c-812d-5937981dbb92" class="numbered-list" start="1">
          <li>A negative control should be conducted of phage with a lack of binding protein,
            or with known inert protein.</li>
        </ol>
      </li>
    </ol>
    <ol type="1" id="f845cdd4-25d3-4fe7-9e44-ba3cb9dd4579" class="numbered-list" start="3">
      <li>Perform pull-down assay as per the above mentioned protocol, collecting the first
        flowthrough, wash, and elutes.</li>
    </ol>
    <ol type="1" id="22756c2f-6d70-4246-8b96-c62aac281c50" class="numbered-list" start="4">
      <li>Titer the flowthrough samples collected and conduct a plaque assay with them, as per the
        above mentioned protol.</li>
    </ol>
    <ol type="1" id="4396ba5e-66c3-44dc-9ae0-cf92021881b0" class="numbered-list" start="5">
      <li>Compare the formation of plaques agains the control, paying close attention to the first
        flowthrough (unbound phage) and elute (bound phage). <ol type="a"
          id="d5e21d28-e62c-455a-a269-259016647532" class="numbered-list" start="1">
          <li>Don’t make direct conclusions too early, one might not see any change in the
            flowthrough if starting phage concentrations are too high, and one might not see
            plaques in the elute if the protein somehow prevents phage infection through
            binding. </li>
        </ol>
      </li>
    </ol>
    </div>
  )
}
function PullDown () {
   return (
    <div>
    <h2 id="ebf39be7-6004-4d1d-b524-49677688ca35" className="">Description</h2>
    <p id="48b5dfee-e7e7-4100-89d1-ec2fc94e8108" className="">A tried and tested method for testing
      protein interactions in solution, this assay is relatively simple to perform and can be done
      with a range of sample types, essentially being an extention of affinity tag purification.
      The principle is simple, a “bait” protein catches the “prey” protein, and are together
      attached to the affinity column, the bait protein can either be pre-attached to the column,
      or attached after incubation with the prey.
      The most ideal way to do this, if using purified proteins, is to use two separate
      purification tags (such as His and GST) so that one protein will not interact with the other
      column, or you can remove the affinity tag from the prey, such as by using <a
        href="http://parts.igem.org/Part:BBa_K4602430">BBa_K4602430</a>. One could also use cell
      lysate as prey to find novel interactions or those with hard-to-purify proteins.</p>
    <h2 id="a771e224-9ce8-4a16-9538-afc0d326f091" className="">Materials</h2>
    <ul id="d4ce1707-3882-48ac-ba80-a77b932c9a9c" className="bulleted-list">
      <li >Purified His-tagged bait protein<ul
          id="a868d329-27e4-44e7-b263-4f5406598d01" className="bulleted-list">
          <li >Make sure that the protein buffer is free of
            imidazole to allow binding to column</li>
        </ul>
      </li>
    </ul>
    <ul id="aa46f129-1f03-49ac-85b4-7b87775c2516" className="bulleted-list">
      <li >Untagged prey protein, or cell lysate<ul
          id="da8b72e1-ada1-4c77-bc26-75a9ae3b0879" className="bulleted-list">
          <li >Also make sure buffer is free of imidazole</li>
        </ul>
      </li>
    </ul>
    <ul id="3d8f6af4-de48-49fc-b41a-300a6470abc9" className="bulleted-list">
      <li >Ni-NTA affinity purification agarose</li>
    </ul>
    <ul id="dc815e21-8c3a-43a6-9877-14910f140c66" className="bulleted-list">
      <li >Gravity flow column for protein purification</li>
    </ul>
    <ul id="f8930281-c4c3-41ce-866e-f49d24405071" className="bulleted-list">
      <li >Buffer A</li>
    </ul>
    <ul id="7b0c7111-fef8-4e0c-b094-8d0bd4e9c297" className="bulleted-list">
      <li >Elution buffer (90% Buffer B, 10% Buffer A)</li>
    </ul>
    <h2 id="f69e24d3-b9c0-417a-a2dd-0336f8b28d90" className="">Methods</h2>
    <ol type="1" id="ac2b8889-370a-4075-a6d1-bea4449de990" className="numbered-list" start="1">
      <li>Incubate 50 µg purified bait protein with 50 µg prey protein in 400 µL total volume of
        neutral buffer free of imidazole, for 1.5 hrs at 30°C with shaking(adjust time and
        temperature according to protein characteristics.<ol type="a"
          id="10d9b4b6-44d0-4f3c-991f-0700998e6c1c" className="numbered-list" start="1">
          <li>If using cell lysate as prey, it is recommended to use around 125 ~ 150 µg of
            prey protein per 1 µg of bait protein.</li>
        </ol>
      </li>
    </ol>
    <ol type="1" id="f0fbc475-cfce-43f5-ac02-92afe52ab703" className="numbered-list" start="2">
      <li>Centrifuge 400 µL Ni-NTA affinity agarose solution at 1000 g for 5 minutes and remove
        supernatant. The solution usually contains some ethanol which can interfere with protein
        interactions and even affect folding, so washing with neutral buffer can be recommended.
      </li>
    </ol>
    <ol type="1" id="a3405864-c9f5-46a5-9f1f-e37b9536a75e" className="numbered-list" start="3">
      <li>Incubate protein mixture with 200 µL of Ni-NTA affinity agarose (buffer removed) for 30
        min. Keep mixture cold and mix gently frequently, or with gentle rotation.</li>
    </ol>
    <ol type="1" id="49fef2ef-ba1d-458a-ab6a-029ee08eab6a" className="numbered-list" start="4">
      <li>Add mixture to gravity flow column and collect flowthrough (centrifuge if applicable for
        column).</li>
    </ol>
    <ol type="1" id="da9a11e2-ceab-42d2-b26c-e6beda1ddcde" className="numbered-list" start="5">
      <li>Wash column with 5 ml buffer A 3 times, and collect flowthrough.</li>
    </ol>
    <ol type="1" id="40a0b2d6-1512-4989-90e3-73ed34c69f1f" className="numbered-list" start="6">
      <li>Add 1 ml elution buffer and collect flowthrough.</li>
    </ol>
    <ol type="1" id="1c7a299d-c14c-4055-8b4a-36903b2bab19" className="numbered-list" start="7">
      <li>Analyse flowthrough with SDS-PAGE or other protein quantification method.</li>
    </ol>
    </div>
   )
}
function ProteinPurification () {
  return (
          <div>
        <h2 id="cd080881-8967-41f6-bc16-f587ddbf6e6e" className="">
          Materials
        </h2>
        <p id="64f04293-fd6c-4452-a4f1-4282193e6c3e" className="">
          Please refer to the buffer preparation section.
        </p>
        <h2 id="50c9a9b7-9a7e-4d1f-b252-8a2faff19505" className="">
          Methods
        </h2>
        <h3 id="691d471d-68f7-4517-876b-310fd81c7e03" className="">
          <span style={{ borderBottom: '0.05em solid' }}>
            <strong>Day 1: Culture preparation and protein expression.</strong>
          </span>
        </h3>
        <ol type="1" id="7052d094-f24d-4b18-bee5-138573c48c08" className="" start="1">
          <li>
            Pick a colony from the BL21 strain of your choice and allow it to grow in 10 ml of LB medium for 4 hours at 37°C with shaking at 220 rpm.
          </li>
        </ol>
        <ol type="1" id="a8e331d1-1178-4235-8982-ada938c9958f" className="" start="2">
          <li>
            Scale up the culture to 1 liter and continue incubating at 37°C with shaking at 220 rpm until the OD600 reaches 0.5-0.6.
          </li>
        </ol>
        <ol type="1" id="5d8e3ec4-7438-48dd-a051-461a8400e085" className="" start="3">
          <li>
            Cool the culture by placing it on ice for 10 minutes, then induce protein expression by adding 0.3 mmol/L IPTG. Incubate the culture with shaking at 200 rpm at 18°C for 20 hours.
          </li>
        </ol>
        <h2 id="491c8f82-b0ed-4036-8f3e-f6db5faf126f" className="">
          <span style={{ borderBottom: '0.05em solid' }}>
            <strong>Day 2: Culture PURE plasmid (followed the protocol one-pot), purification and desalting.</strong>
          </span>
        </h2>
        <ol type="1" id="62380123-ba33-40d0-b908-9848ac0481f5" className="" start="1">
          <li>
            Harvest the BL21 cells by centrifugation at maximum speed (at 4°C) for 10 minutes.
          </li>
        </ol>
        <ol type="1" id="85bff6d5-f0fc-488c-ba95-9ec23909f688" className="" start="2">
          <li>
            Use 20 ml of chemical lysis buffer for every 1 liter of culture and incubate at 37°C with shaking for 30 minutes.
          </li>
        </ol>
        <ol type="1" id="772a313e-dd59-4d55-87f4-388175837e6e" className="" start="3">
          <li>
            During this time, prepare 2 mL of Ni-NTA agarose gel (Machery Nagel, #). Gently mix the gel, transfer it to a 15 mL Falcon tube, and centrifuge for 2 minutes at 1000g. Carefully remove the supernatant and add 5 mL of buffer A. Keep this solution on ice.
          </li>
        </ol>
        <ol type="1" id="da0d1257-5437-4546-9bde-0aa56b8a3d77" className="" start="4">
          <li>
            Aliquot the lysed cells into 2 mL tubes and centrifuge using a benchtop centrifuge at 4°C for 30 minutes at maximum speed.
          </li>
        </ol>
        <ol type="1" id="09280713-faf6-4e7a-acdb-c2dd3e47715c" className="" start="5">
          <li>
            Add the supernatant to the equilibrated Ni-NTA agarose gel along with 40 ml of Buffer A. Incubate on ice for 30 minutes with rotation. While waiting, prepare all the necessary buffers for purification and desalting.
          </li>
        </ol>
        <ol type="1" id="d4ce2d43-f502-4930-827e-a1f76cd2c034" className="" start="6">
          <li>
            Load the entire sample into a Portino column (Machery Nagel, #) with a filter paper already in place. Allow the liquid to pass through and add 25 mL of buffer A. Let it flow through.
          </li>
        </ol>
        <ol type="1" id="20f82c4d-61f7-43b5-a8c1-2feddb6f6aa4" className="" start="7">
          <li>
            Wash: Wash the column with 25 mL of a mixture of 23.75 mL buffer A and 1.25 mL buffer B, resulting in a 25 mM imidazole buffer. Add the mixture to the column and allow it to flow through.
          </li>
        </ol>
        <ol type="1" id="8ea1f63d-9e8b-40c9-841c-b6b52753a1cd" className="" start="8">
          <li>
            Elute: Elute with 5 mL of a mixture of 0.5 mL buffer A and 4.5 mL buffer B, resulting in a 450 mM imidazole buffer. Place a new 15 mL Falcon tube beneath the column, add the elution buffer, and collect the eluate. You can use 2.5 mL instead of 5 mL to save time for desalting. If you use 5 mL, and desalting is needed (max volume of 2.5 mL), concentrate the sample using Amicon filters.
          </li>
        </ol>
        <ol type="1" id="0f586123-1745-40c5-adaf-86290c710685" className="" start="9">
          <li>
            For buffer exchange, use PD-10 desalting columns (size exclusion chromatography) instead of Amicon filters. Ensure the smaller protein in PURE (around 10 kDa) is compatible with the separation.
          </li>
        </ol>
        <ol type="1" id="bdabb325-009e-404a-8b98-e3acd1b254fd" className="" start="10">
          <li>
            Remove the top cap of the PD-10 column and pour off the column storage solution.
          </li>
        </ol>
        <ol type="1" id="cd9a845d-8883-43ef-bcd0-b24890a74966" className="" start="11">
          <li>
            Cut the sealed end of the column at the notch.
          </li>
        </ol>
        <ol type="1" id="240a57a7-eacb-49a6-8ce2-8979d5984997" className="" start="12">
          <li>
            Fill the column with 5 ml of desalting/storage buffer (the same as HT buffer in the table above) and allow it to drain by gravity. Repeat this step four times and discard the flow-through.
          </li>
        </ol>
        <ol type="1" id="30132f38-02f8-4a48-b6c3-2ca30059744f" className="" start="13">
          <li>
            Add a maximum of 2.5 ml of sample to the column. Allow the sample or equilibration buffer to enter the packed bed completely and discard the flow-through.
          </li>
        </ol>
        <ol type="1" id="48052f04-b0f6-40bf-8d67-5d0269f6b99b" className="" start="14">
          <li>
            Place a test tube for sample collection under the column. Elute with 3.5 ml of buffer (equilibrium/HT) and collect the eluate.
          </li>
        </ol>
        <ol type="1" id="38e162fe-ebe5-4791-bb15-c0c57374fb1f" className="" start="15">
          <li>
            Use an Amicon filter Ultra 15 (3 kDa MWCO) to concentrate the sample. Add an equal volume of stock buffer B (60% glycerol) to achieve a final protein concentration of 30% glycerol for storage.
          </li>
        </ol>
        <ol type="1" id="8b9f6794-bbe5-45d2-a72c-6a8b3288fc42" className="" start="16">
          <li>
            Aliquot the samples and flash freeze them in liquid nitrogen, then store them at -80°C (100 µl per well).
          </li>
        </ol>
        <ol type="1" id="b95c4116-ce52-4255-b372-4aae4adc66de" className="" start="17">
          <li>
            Measure the concentration using a nanodrop.
          </li>
        </ol>
        <ol type="1" id="8288802b-34b6-462c-9d81-9320be8a0daf" className="" start="18">
          <li>
            Recycle the PD-10 desalting columns by washing them with 10 ml of dH2O, followed by 25 ml of 20% ethanol. Cap the input and output before storing.
          </li>
        </ol>
      </div>
    );
  
}
function SDSPAGE () {
  return (
    <div>
    <h2 id="0b354f8d-03fb-4763-bf5c-c869b89ffaba" class="">Description</h2>
    <p id="4ea9e85f-a56d-42f8-ab0f-eb05f9284daa" class="">A standard method for assessing the size
      of proteins in solution. Proteins are separated by weight though electrophoresis. SDS-PAGE
      denatures the protein structure and allows for true assessment of size, while Native-PAGE
      retains the protein structure and activity, potentially allowing for the visualisation of
      protein interactions. Note that standard protein ladders will not work with Native-PAGE and
      so adequate controls are required for proper assessment. Additionally note that modified
      proteins (such as highly glycosylated mucins) cannot be analysed properly through standard
      PAGE protocols.</p>
    <h2 id="f7f04d08-601e-499a-89ea-4e877387e68a" class="">Materials</h2>
    <ul id="791f1e89-8b1e-4eec-939d-b3e638db0d2d" class="bulleted-list">
      <li style={{listStyleType:"disc"}}>1x Running buffer (SDS or Native, choose adequately)</li>
    </ul>
    <ul id="b94f84c4-0955-4261-9420-7d4c56979e9f" class="bulleted-list">
      <li style={{listStyleType:"disc"}}>Sample buffer (Laemmli sample buffer for denaturing
        SDS-PAGE or Native sample buffer for Native-PAGE)</li>
    </ul>
    <ul id="64cc6229-f314-492b-96d3-f3b7e6bb6922" class="bulleted-list">
      <li style={{listStyleType:"disc"}}>Pre-cast Mini-PROTEAN TGX gel of adequate percentage <ul
          id="5f4309c4-97c8-4810-a33a-c4f7b0aa253c" class="bulleted-list">
          <li style={{listStyleType:"circle"}}>(4 - 20% gradient gel is recommended for unknown
            proteins or mixtures with a large range of protein size)</li>
        </ul>
        <ul id="27bbdc64-e0d0-4427-855e-78cfde5b3fd5" class="bulleted-list">
          <li style={{listStyleType:"circle"}}>Alternatively you can cast your own
            polyacrylamide gels.</li>
        </ul>
      </li>
    </ul>
    <ul id="695d2cf5-7d27-4a83-9977-6d7351ef6e7f" class="bulleted-list">
      <li style={{listStyleType:"disc"}}>Mini-PROTEAN Tetra cell electrophoresis module</li>
    </ul>
    <ul id="3a2f7bdf-851d-4e23-b6ac-580647103c4d" class="bulleted-list">
      <li style={{listStyleType:"disc"}}>Coomassie Brilliant Blue Staining Solution</li>
    </ul>
    <h2 id="4babccc0-abf8-4a84-83b0-acb6ee0ebd6e" class="">Methods</h2>
    <ol type="1" id="20c48734-7c36-4a73-985f-9091956f30b0" class="numbered-list" start="1">
      <li><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong>Prepare
                                        gels:</strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong>
        Gently remove the comb from the top of the gel, being careful not to disturb the well
        structures. Remove the tape from the bottom of the gel (DO NOT FORGET THIS STEP)</li>
    </ol>
    <ol type="1" id="86799165-25db-429c-8aab-a8140dc9aa9e" class="numbered-list" start="2">
      <li>Assemble the electrophoresis cell, making sure to insert the gel all the way down, and
        in the correct orientation.</li>
    </ol>
    <ol type="1" id="9bf8392e-b81e-4f57-a099-17f65e084cec" class="numbered-list" start="3">
      <li>Fill the inner buffer chamber with runing buffer up until the wells of the gel are fully
        submerged. fill the lower buffer chamber to the indicator mark.</li>
    </ol>
    <ol type="1" id="82106340-4420-4095-9f5f-6daf6b5185cb" class="numbered-list" start="4">
      <li><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong>Prepare
                                                the
                                                samples:</strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong>
        Add 5 µL of sample to 4.75 µL sample buffer and 0.25 µL β-mercaptoethanol. (do not add
        β-mercaptoethanol if running Native-PAGE)<ol type="a"
          id="980d8182-0caf-435d-830f-5eb613ebfbdf" class="numbered-list" start="1">
          <li>β-mercaptoethanol produces toxic fumes and should only be opened under a
            chemical fume hood.</li>
        </ol>
      </li>
    </ol>
    <ol type="1" id="8d59e006-164c-4d89-9468-44a9a57aa890" class="numbered-list" start="5">
      <li>Heat the samples at 90–100°C for 5 min. Skip this step if running Native-PAGE.<ol
          type="a" id="a97c6a37-c8cc-4668-992e-157a61d242fb" class="numbered-list" start="1">
          <li>β-mercaptoethanol produces toxic fumes and should only be opened under a
            chemical fume hood.</li>
        </ol>
        <ol type="a" id="f56796f3-655a-4436-a756-82e093097c76" class="numbered-list" start="2">
          <li>Large sample volumes compared to tube size can cause the lid of the tube to pop
            at this stage, pierce a hole in the top of the tube to prevent this.</li>
        </ol>
      </li>
    </ol>
    <ol type="1" id="b1b694a9-2e76-4e6c-abba-486d0b6ff6d6" class="numbered-list" start="6">
      <li>Load sample gently into the wells of the gel, being careful not to overflow to adjascent
        wells.</li>
    </ol>
    <ol type="1" id="1afda1cd-fcc5-48dd-83ab-edac3111c7ea" class="numbered-list" start="7">
      <li>Connect electrophoresis cell to power supply, and run at 200V for around 30 minutes,
        until the blue leading edge almost reaches the black line at the bottom of the gel.<ol
          type="a" id="f7bfb4a3-d67e-42be-9eba-bdd494966b18" class="numbered-list" start="1">
          <li>The current should be around 35–50 mA initially and 20–31 mA at the end.</li>
        </ol>
      </li>
    </ol>
    <ol type="1" id="51ed60cf-a51e-426c-8b4d-226ac76417b1" class="numbered-list" start="8">
      <li>Turn off the power and remove the gel.</li>
    </ol>
    <ol type="1" id="44ca3be1-db6b-4e2a-bde6-fd8bd1a11325" class="numbered-list" start="9">
      <li>Break open the gel casing. We recommend cutting a small marking on one side of the gel
        for easy future orientation of the well.</li>
    </ol>
    <ol type="1" id="7143ffd4-4bd5-4016-815a-0999d488284d" class="numbered-list" start="10">
      <li>Place gel in a vessel and wash 3 times with distilled water.</li>
    </ol>
    <ol type="1" id="e127740a-f443-494f-8a34-81f6314fd9e7" class="numbered-list" start="11">
      <li><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong><strong>Staining
                                          the
                                          gel:</strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong></strong>
        Add enough Coomassie Brilliant Blue staining solution to cover the gel, incubate for 30
        mins at room temp with gentle rocking.</li>
    </ol>
    <ol type="1" id="cc7d2adc-0778-4209-9935-3ab56de349d8" class="numbered-list" start="12">
      <li>Remove staining solution, wash gel 3 times with distilled water.</li>
    </ol>
    <ol type="1" id="80f5915a-a4a3-4ca3-9a5c-da4ae40a717f" class="numbered-list" start="13">
      <li>Add plenty of distilled water to the vessel and incubate for &gt; 1 hr with gentle
        shaking, until bands are clearly visible.<ol type="a"
          id="e5059e57-7a45-41f7-8312-889396f3777e" class="numbered-list" start="1">
          <li>Frequent water changes can reduce the time taken for band visualisation.</li>
        </ol>
      </li>
    </ol>
    <ol type="1" id="57918636-7196-4f74-9f3b-e89eb5db3500" class="numbered-list" start="14">
      <li>Image the gels and analyse.</li>
    </ol>
  </div>
  )
}
function PseudoELISA () {
  return (
    <div>
    <h2 id="6ddbc2a8-f07f-4a8b-b2ed-9d17da7dd006" class="">Description</h2>
    <p id="539f6f5f-d63e-4d55-8a97-d39b5b31f15a" class="">In order to easily assess the activity of
      our proteins as a fusion construct, every candidate protein was purified as a sfGFP-fusion
      product. For this reason we could perform ELISA-like assays without the use of antibodies,
      rather just measuring fluorescence intensity directly. (With some more time and a validated
      anti-sfGFP antibody, we can also continue the assessment into a full ELISA). We specifically
      chose this method for our mucin binding assays, as these highly glycosylated proteins can be
      troublesome when assessed in solution (such as in a pull-down assay).</p>
    <p id="90cb6080-6aa5-473c-83b0-7f15dbb567e8" class="">
      <strong><strong><strong><strong><strong><strong>Note:
                </strong></strong></strong></strong></strong></strong>The buffer
      composition and pH, among other variables, can highly affect protein interactions, don’t be
      afraid to experiment around!</p>
    <h2 id="f0f42858-caaf-44ab-9911-1895064cdccf" class="">Materials</h2>
    <ul id="c3617ee7-ccfe-43e5-9ea5-11d0c1414868" class="bulleted-list">
      <li style={{listStyleType:"disc"}}>96-well ELISA plates (high-binding)</li>
    </ul>
    <ul id="e08dbba5-db82-4e2e-b0b4-44671b65e564" class="bulleted-list">
      <li style={{listStyleType:"disc"}}>“Bait” proteins at desired concentrations</li>
    </ul>
    <ul id="9f57920b-815a-4623-a09c-e31acda411dd" class="bulleted-list">
      <li style={{listStyleType:"disc"}}>“Prey” proteins at desired concentrations</li>
    </ul>
    <ul id="767bcd78-389c-47b2-b54e-35039f8c1c08" class="bulleted-list">
      <li style={{listStyleType:"disc"}}>Phosphate-buffered saline (PBS)</li>
    </ul>
    <ul id="69b4238f-162b-4534-a477-50bd9db21676" class="bulleted-list">
      <li style={{listStyleType:"disc"}}>Blocking buffer<ul
          id="d44d8a01-252e-4a60-a099-4e3f562e0a24" class="bulleted-list">
          <li style={{listStyleType:"circle"}}>1% BSA</li>
        </ul>
        <ul id="55059a9c-55b1-4d91-84ab-f4a0967fef47" class="bulleted-list">
          <li style={{listStyleType:"circle"}}>1x Tris-buffered saline (TBS)</li>
        </ul>
        <ul id="6fae5f96-ebe5-4390-8e58-96432f012f73" class="bulleted-list">
          <li style={{listStyleType:"circle"}}>0.1% Tween 20</li>
        </ul>
      </li>
    </ul>
    <h2 id="baa78cf7-4b72-42af-a4cc-f6cac2778c52" class="">Methods</h2>
    <ol type="1" id="f8083172-5af4-4c89-aab0-44f71281710e" class="numbered-list" start="1">
      <li><strong><strong><strong>Coat: </strong></strong></strong>Add 50 ~ 150 µL “bait” protein
        at desired concentrations to the wells of the ELISA plate, and incubate overnight at
        4°C, this will coat each well with the protein of choice.</li>
    </ol>
    <ol type="1" id="8d14a61d-617c-4cda-a0c3-490c7ecf7b59" class="numbered-list" start="2">
      <li>Remove the supernatant by upturning the plate, and tapping GENTLY on a paper towel.<ol
          type="a" id="b3c4f214-7741-413b-a201-4f13d087b525" class="numbered-list" start="1">
          <li>You may have to shake the plate a little during the upturning to remove all of
            the liquid, but be careful not to knock the plate too hard.</li>
        </ol>
      </li>
    </ol>
    <ol type="1" id="f2fb9771-12dd-455a-b7a6-7cd5f80a2e93" class="numbered-list" start="3">
      <li><strong>Wash </strong>the wells 4 times with 150 µL PBS.<ol type="a"
          id="ea7ea438-7566-45dd-88e2-173d221deeed" class="numbered-list" start="1">
          <li>For every wash step during the experiment, fill the well with PBS and wait
            around 10 ~ 60 seconds, before upturning the plate to remove the liquid and
            tapping gently on a paper towel. Try to keep the wash time uniform across
            samples.</li>
        </ol>
      </li>
    </ol>
    <ol type="1" id="af06dadb-b76a-48b7-a41f-9940f9fc724e" class="numbered-list" start="4">
      <li><strong><strong><strong><strong><strong><strong><strong>Block:
                    </strong></strong></strong></strong></strong></strong></strong>Add
        150 µL blocking buffer to each well and incubate for 1 ~ 2 hrs at room temp with
        shaking. This will block the uncoated sites on the wells to prevent non-specific
        binding.</li>
    </ol>
    <ol type="1" id="9badc7ce-91dc-4a2b-a244-6d72361b6df9" class="numbered-list" start="5">
      <li>Remove the blocking buffer and <strong><strong><strong><strong><strong>wash
                </strong></strong></strong></strong></strong>the wells 4 times with 150
        µL PBS.</li>
    </ol>
    <ol type="1" id="405028b3-52dd-43bc-8d17-c0cd6ced2fcc" class="numbered-list" start="6">
      <li><strong><strong><strong><strong><strong>Bind:
                </strong></strong></strong></strong></strong>Add 50 ~ 150 µL of binding
        protein at desired concentration, incubate at desired temperature and time depending on
        the protein of choice.<ol type="a" id="6a8cd52b-b807-432c-bd22-58239abd06df"
          class="numbered-list" start="1">
          <li>We incubated our binding proteins overnight at 30°C, but adjust accordingly
            depending on the stability etc. of the protein.</li>
        </ol>
        <ol type="a" id="5110fefd-7be7-4039-a3f1-953465665221" class="numbered-list" start="2">
          <li>We recommend measuring fluorescence / absorbance after adding the proteins, to
            give you confirmation of the starting concentrations.</li>
        </ol>
      </li>
    </ol>
    <ol type="1" id="b3a2c2c4-413a-498d-bf2b-1093bfe2270c" class="numbered-list" start="7">
      <li><strong>Wash </strong>the wells 4 times with 150 µL PBS.</li>
    </ol>
    <ol type="1" id="59029b61-2200-4528-8420-287cb817637b" class="numbered-list" start="8">
      <li><strong><strong><strong><strong><strong><strong><strong>Measure</strong></strong></strong></strong></strong></strong></strong>
        the fluorescence / absorbance of the wells in a 96-well plate reader.<ol type="a"
          id="cc262905-d513-40a4-aed1-9b6a1fbc8d82" class="numbered-list" start="1">
          <li>Most plate readers have the option to take multiple readings per well and take
            an average value, we recommend this to get a more reliable output less dependent
            on coating efficiency.</li>
        </ol>
      </li>
    </ol>
</div>
  )
}
const Protocols = () => {

    const dataProtocol = [
        {
            header: "Chemical competent cells and transformation",
            component: Chemicals
        },
        {
            header: "Electrocompetent cells and Electroporation",
            component: Electrocompetent

        },
        {
            header: "Gibson assembly",
            component: Gibson

        },
        {
            header: "Polymerase chain assembly (PCA)",
            component: PCA

        },
        {
            header: "Colony PCR",
            component: ColonyPCA

        },
        {
            header: "Goldengate cloning",
            component: goldenGate

        }
    ]
    const dataProtocol2 = [
        {
            header: "Buffers for purification / pull-down",
            component: BuffersPurification
        },
        {
            header: "Protein Purification",
            component: ProteinPurification

        },
        {
            header: "Pull-down protein interaction Assay",
            component: PullDown

        },
        {
            header: "Pseudo-ELISA protein interaction assay",
            component: PseudoELISA

        },
        {
          header: "SDS-PAGE / Native-PAGE",
          component: SDSPAGE
        }
      ]
        const dataProtocol3 = [
            {
                header: "Phage Amplification",
                component: Amplification
            },
            {
                header: "Plaque Assay",
                component: Plaque
    
            },
            {
                header: "12 Well plate bilayer Plaque Assay",
                component: bilayerAssay
    
            },
            {
                header: "Phage retention pull-down assay",
                component: PhageRetention
    
            }
        ]

    return (
        <div>
            <BigHeader headerText={'Protocols'} backgroundImage={'https://static.igem.wiki/teams/4602/wiki/images/images/banner.png'}/>
            <h1>Genetic</h1>
            {dataProtocol.map((data, index) => (
            <OneAcc text={data.header} component={<data.component/>}/>
            ))}
            <h1>Protein</h1>
            {dataProtocol2.map((data, index) => (
            <OneAcc text={data.header} component={<data.component/>}/>
            ))}
            <h1>Phage</h1>
            {dataProtocol3.map((data, index) => (
            <OneAcc text={data.header} component={<data.component/>}/>
            ))}
        </div>
    )
}
export default Protocols;